/*
 * // Created on 2023-11-16
 * // Created by Vitaut Tryputsin
 * //
 * // Copyright (c) 2023 OPEN CASCADE SAS
 * //
 * // This file is part of commercial software by OPEN CASCADE SAS,
 * // furnished in accordance with the terms and conditions of the contract
 * // and with the inclusion of this copyright notice.
 * // This file or any part thereof may not be provided or otherwise
 * // made available to any third party.
 * //
 * // No ownership title to the software is transferred hereby.
 * //
 * // OPEN CASCADE SAS makes no representation or warranties with respect to the
 * // performance of this software, and specifically disclaims any responsibility
 * // for any damages, special or consequential, connected with its use.
 *
 */
import { Mat4Tools } from "../../../core/geometry/mat4-tools.js";
import { OperationSide, SelectionOption, CTMode, } from "./patient-info.js";
import { Vec3Tools } from "../../../core/geometry/vec3-tools.js";
import { StemHeadUtilities } from "./stem-head-utilities.js";
function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map((item) => deepClone(item));
    }
    if (typeof obj === 'object') {
        const clonedObject = {};
        for (const key in obj) {
            // @ts-ignore
            if (obj.hasOwnProperty(key)) {
                clonedObject[key] = deepClone(obj[key]);
            }
        }
        return clonedObject;
    }
    return obj;
}
export class PatientContextReader {
    /**
     * Read patient context from parsed JSON.
     * @param theJson - parsed JSON.
     * @param theUnitsScale - units scale.
     * @param theSystemTransform - system transformation.
     * @param theOperationSide - operation side.
     * @returns patient context.
     */
    static readPatientJson(theJson, theUnitsScale = 1000, theSystemTransform = [
        [1, 0, 0, 0],
        [0, 0, -1, 0],
        [0, 1, 0, 0],
        [0, 0, 0, 1],
    ], theOperationSide = OperationSide.Right, cupScale = 25.4) {
        const ts = theJson.transforms;
        //! Transform transformations
        const aTrans = (theMat) => {
            if (Mat4Tools.isValid(theMat)) {
                return Mat4Tools.multiply(theSystemTransform, Mat4Tools.scaleUnits(Mat4Tools.transpose(theMat), theUnitsScale));
            }
            return Mat4Tools.identity();
        };
        //! Read transformations
        const aTransformations = {
            pelvisTGlobal: aTrans(ts.pelvisTGlobal),
            femurLeftTGlobal: aTrans(ts.femurLeftTGlobal),
            femurRightTGlobal: aTrans(ts.femurRightTGlobal),
            stemTGlobal: aTrans(ts.stemTGlobal),
            shellTGlobal: aTrans(ts.cupTGlobal),
            linerTGlobal: aTrans(ts.cupTGlobal),
            femurRightPreopTGlobal: aTrans(ts.femurRightPreopTGlobal),
            femurLeftPreopTGlobal: aTrans(ts.femurLeftPreopTGlobal),
            headTGlobal: Mat4Tools.identity(),
            insertTGlobal: Mat4Tools.identity(),
        };
        //! Read landmarks data
        const l = theJson.landmarks;
        //! Process landmarks data
        const aPL = (theLandmarks) => {
            if (theLandmarks && theLandmarks.length == 3) {
                let aPnt = { x: theLandmarks[0], y: theLandmarks[1], z: theLandmarks[2] };
                aPnt = Vec3Tools.multiply(aPnt, theUnitsScale);
                aPnt = Vec3Tools.applyMatrix(theSystemTransform, aPnt);
                return aPnt;
            }
            return Vec3Tools.invalid();
        };
        //! Read landmarks
        const aLandmarks = {
            pelvisLeftCorLandmark: aPL(l.pelvisLeftCorLandmark),
            pelvisRightCorLandmark: aPL(l.pelvisRightCorLandmark),
            pelvisLeftAsisLandmark: aPL(l.pelvisLeftAsisLandmark),
            pelvisRightAsisLandmark: aPL(l.pelvisRightAsisLandmark),
            pelvisLeftPubicSymphysisLandmark: aPL(l.pelvisLeftPubicSymphysisLandmark),
            pelvisRightPubicSymphysisLandmark: aPL(l.pelvisRightPubicSymphysisLandmark),
            femurLeftCorLandmark: aPL(l.femurLeftCorLandmark),
            femurRightCorLandmark: aPL(l.femurRightCorLandmark),
            femurLeftLtLandmark: aPL(l.femurLeftLtLandmark),
            femurRightLtLandmark: aPL(l.femurRightLtLandmark),
            femurLeftLtProxLandmark: aPL(l.femurLeftLtProxLandmark),
            femurRightLtProxLandmark: aPL(l.femurRightLtProxLandmark),
            femurLeftGtLandmark: aPL(l.femurLeftGtLandmark),
            femurRightGtLandmark: aPL(l.femurRightGtLandmark),
            femurLeftCondyleMedLandmark: aPL(l.femurLeftCondyleMedLandmark),
            femurLeftCondyleLatLandmark: aPL(l.femurLeftCondyleLatLandmark),
            femurRightCondyleMedLandmark: aPL(l.femurRightCondyleMedLandmark),
            femurRightCondyleLatLandmark: aPL(l.femurRightCondyleLatLandmark),
            femurLeftAxisStartLandmark: aPL(l.femurLeftAxisStartLandmark),
            femurLeftAxisEndLandmark: aPL(l.femurLeftAxisEndLandmark),
            femurRightAxisStartLandmark: aPL(l.femurRightAxisStartLandmark),
            femurRightAxisEndLandmark: aPL(l.femurRightAxisEndLandmark),
        };
        //! read plan details
        const pd = theJson.planDetails;
        const aPlanDetails = {
            spmClassification: pd.spmClassification,
            pelvicTilt: pd.pelvicTilt,
            sittingSacralSlope: pd.sittingSacralSlope,
            standingSacralSlope: pd.standingSacralSlope,
            standingLumbarLordosis: pd.standingLumbarLordosis,
            sittingLumbarLordosis: pd.sittingLumbarLordosis,
            cupInclinationAngle: pd.cupInclinationAngle,
            cupAnteversionAngle: pd.cupAnteversionAngle,
            stemAnteversionAngle: pd.stemAnteversionAngle,
            combinedAnteversionAngle: pd.combinedAnteversionAngle,
            referencePlane: pd.referencePlane,
            legLengthPreop: pd.legLengthPreop,
            legLengthOpposite: pd.legLengthOpposite,
            legOffsetPreop: pd.legOffsetPreop,
            legOffsetOpposite: pd.legOffsetOpposite,
            corShiftSuperiorPreop: pd.corShiftSuperiorPreop,
            corShiftMedialPreop: pd.corShiftMedialPreop,
            corShiftPosteriorPreop: pd.corShiftPosteriorPreop,
            corShiftSuperiorOpposite: pd.corShiftSuperiorOpposite,
            corShiftMedialOpposite: pd.corShiftMedialOpposite,
            corShiftPosteriorOpposite: pd.corShiftPosteriorOpposite,
            neckResectionDepth: pd.neckResectionDepth,
        };
        //! Head info
        const aHeadInfo = {
            offset: 0.0,
            neckLength: 0.0,
            scale: cupScale,
            stemInterfacePlane: {
                location: { x: 0.0, y: 0.0, z: 0.0 },
                normal: { x: 0.0, y: 0.0, z: 1.0 },
            },
        };
        //! Insert info
        const anInsertInfo = { scale: 0, rotationPoint: { x: 0, y: 0, z: 0 } };
        //! Stem info
        const aStemInfo = {
            scale: 1.0,
            stemEndPoint: { x: 0.0, y: 0.0, z: 0.0 },
            stemNeckPoint: { x: 0.0, y: 0.0, z: 0.0 },
            headEndPoint: { x: 0.0, y: 0.0, z: 0.0 },
            resectionPlane: StemHeadUtilities.get3PointPlane({ x: 0.0, y: 0.0, z: 0.0 }, { x: 1.0, y: 0.0, z: 0.0 }, { x: 0.0, y: 1.0, z: 0.0 }),
        };
        aTransformations.headTGlobal = Mat4Tools.identity();
        aTransformations.insertTGlobal = Mat4Tools.identity();
        //! Custom transformations of patient
        const aCustomPatientTransformations = {
            adjustViewStem: deepClone(aTransformations.stemTGlobal),
            adjustViewLiner: deepClone(aTransformations.linerTGlobal),
            adjustViewShell: deepClone(aTransformations.shellTGlobal),
            adjustViewFemur: theOperationSide === OperationSide.Right
                ? deepClone(aTransformations.femurRightTGlobal)
                : deepClone(aTransformations.femurLeftTGlobal),
            adjustViewHead: deepClone(aTransformations.headTGlobal),
            adjustViewInsert: deepClone(aTransformations.insertTGlobal),
        };
        //! Ingeniering info
        const aPatientEngineeringInfo = {
            transform: deepClone(aTransformations),
            landmarks: deepClone(aLandmarks),
            planDetails: deepClone(aPlanDetails),
            stemInfo: aStemInfo,
            shellInfo: {
                rotationPoint: { x: Infinity, y: Infinity, z: Infinity },
                scale: 0,
                liEpOffset: 0,
            },
            linerInfo: {
                rotationPoint: { x: Infinity, y: Infinity, z: Infinity },
                scale: 0,
            },
            headInfo: aHeadInfo,
            insertInfo: anInsertInfo,
            operationSide: theOperationSide,
            rotationMatrix: theSystemTransform,
            unitsScale: theUnitsScale,
            //! x - right, y - up, z - forward,
            coordinateSystemTransform: [
                [1, 0, 0, 0],
                [0, 0, 1, 0],
                [0, 1, 0, 0],
                [0, 0, 0, 1],
            ],
            computedTomographyMode: Vec3Tools.isInfinite(aLandmarks.femurLeftCorLandmark) ? CTMode.CTFree : CTMode.CTBased,
        };
        //! Current plan info
        const aPatientCurrentInfo = {
            transform: deepClone(aTransformations),
            landmarks: deepClone(aLandmarks),
            planDetails: deepClone(aPlanDetails),
            customTransform: aCustomPatientTransformations,
            selectionOption: aPlanDetails.referencePlane.toLowerCase() == 'anterior pelvic plane'
                ? SelectionOption.APP
                : SelectionOption.FP,
        };
        //! Patient context
        const aPatient = {
            engineering: aPatientEngineeringInfo,
            current: aPatientCurrentInfo,
        };
        return aPatient;
    }
}
